/**
 * AuditBlock — the Audit section of the Inspector grammar (design-system §5.5 /
 * frontend-display §5.5 / frontend-entity-coverage §5). The inspectability core.
 *
 * Fetches the **generic** `GET /api/{resource}/:id/timeline` and renders the
 * merged event / rule-run / agent-run / decision feed via the shared (de-carded)
 * `Timeline` component. It is built to *degrade, never crash*:
 *
 *   - 404 / not-yet-shipped endpoint → a calm muted TEASER note (the §5.5
 *     `[PROPOSED]` labeled-empty state), never an error, never a blank.
 *   - other failures                 → the same teaser (the audit feed is
 *     advisory; a show page must never hard-error on its account).
 *   - loading                        → height-matched skeleton rows (never a
 *     spinner for content, §7).
 *   - empty `{ items: [] }`          → a muted "no activity yet" line.
 *
 * Above the timeline, an optional **provenance row** lays out causal ids
 * (`produced_by_run_id`, `correlation_id`, `causation_id`, source event) as a
 * compact inline definition list of `RefLink`s (§5.5), so the forward/backward
 * provenance walk starts here.
 *
 * The fetch follows the portal's sanctioned custom-GET pattern (`useApiUrl()` +
 * native `fetch` with `credentials: 'include'`, mirroring `TaskTimeline` /
 * `ContextPanel`) — every byte still rides the boxed worker session.
 */
import * as React from "react";
import { useApiUrl } from "@refinedev/core";
import type { TimelineEntry } from "@aos/shared";

import { cn } from "@/lib/utils";
import { Timeline } from "@/components/app/Timeline";
import { RefLink } from "@/components/inspector/RefLink";

interface TimelineResponse {
  items: TimelineEntry[];
}

type AuditState =
  | { status: "loading" }
  | { status: "ready"; items: TimelineEntry[] }
  /** Endpoint absent / not yet shipped → render the teaser. */
  | { status: "teaser" };

/** A single provenance id → its resolving `RefLink` (or a teaser dash). */
export interface ProvenanceRef {
  label: string;
  /** Target resource path for the id (e.g. `"agent-runs"`, `"events"`). */
  resource: string;
  id: string | number | null | undefined;
}

function useTimeline(
  resource: string | undefined,
  id: string | number | null | undefined,
): AuditState {
  const apiUrl = useApiUrl();
  const [state, setState] = React.useState<AuditState>({ status: "loading" });

  const idStr = id === null || id === undefined ? "" : String(id);
  const enabled = Boolean(resource) && idStr !== "";

  React.useEffect(() => {
    if (!enabled) {
      // No backing target yet → teaser (never an error).
      setState({ status: "teaser" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });

    const url = `${apiUrl}/${resource}/${encodeURIComponent(idStr)}/timeline`;
    fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "include",
    })
      .then(async (res) => {
        if (res.status === 404 || res.status === 501) {
          // The generic timeline endpoint hasn't shipped for this resource.
          if (!cancelled) setState({ status: "teaser" });
          return null;
        }
        if (!res.ok) {
          // Any other failure also degrades to the teaser (advisory surface).
          if (!cancelled) setState({ status: "teaser" });
          return null;
        }
        return (await res.json()) as TimelineResponse;
      })
      .then((body) => {
        if (cancelled || body === null) return;
        setState({
          status: "ready",
          items: Array.isArray(body.items) ? body.items : [],
        });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "teaser" });
      });

    return () => {
      cancelled = true;
    };
  }, [apiUrl, resource, idStr, enabled]);

  return state;
}

/** The muted, never-erroring `[PROPOSED]` teaser (§5.5). */
function AuditTeaser({ resource }: { resource: string | undefined }) {
  const path = resource ? `/api/${resource}/:id/timeline` : "/api/{resource}/:id/timeline";
  return (
    <p className="text-sm text-muted-foreground">
      Audit timeline available once{" "}
      <code className="font-mono text-xs">{path}</code> ships{" "}
      <span className="text-xs">([PROPOSED])</span>.
    </p>
  );
}

export interface AuditBlockProps {
  /** The entity's resource path (Refine `name`, e.g. `"artifacts"`). */
  entityType: string | undefined;
  /** The entity id whose timeline to fetch. */
  entityId: string | number | null | undefined;
  /**
   * Optional provenance ids (causal chain) shown above the timeline as a
   * compact inline definition list of `RefLink`s. Empty entries are skipped.
   */
  provenance?: ProvenanceRef[];
  className?: string;
}

/**
 * `<AuditBlock entityType entityId />` — provenance row + chronological
 * event/run/decision feed. Renders the teaser, never an error, when the generic
 * timeline endpoint is absent.
 */
export function AuditBlock({
  entityType,
  entityId,
  provenance,
  className,
}: AuditBlockProps) {
  const state = useTimeline(entityType, entityId);
  const provRefs = (provenance ?? []).filter(
    (p) => p.id !== null && p.id !== undefined && String(p.id) !== "",
  );

  return (
    <div className={cn("space-y-4", className)}>
      {provRefs.length > 0 ? (
        <dl className="flex flex-wrap gap-x-6 gap-y-1.5 text-xs">
          {provRefs.map((p) => (
            <div key={p.label} className="flex items-baseline gap-1.5">
              <dt className="text-muted-foreground">{p.label}</dt>
              <dd>
                <RefLink resource={p.resource} id={p.id} />
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {state.status === "loading" ? (
        <Timeline items={undefined} loading />
      ) : state.status === "teaser" ? (
        <AuditTeaser resource={entityType} />
      ) : (
        <Timeline
          items={state.items}
          emptyMessage="No audit activity recorded yet."
        />
      )}
    </div>
  );
}

export default AuditBlock;
